import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, IconButton } from '@mui/material';
import { Send, Edit2 } from 'lucide-react';
import { userService } from 'components/api/users';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { formatDate } from 'utils/date';
import styles from './userTabs.module.scss';

interface UserNotesProps {
  userId: string | number;
}

interface Note {
  id: string | number;
  text: string;
  adminId: number;
  createdAt: string;
  updatedAt: string;
  author?: {
    id: number;
    username: string;
    email: string;
  };
}

const UserNotes: React.FC<UserNotesProps> = ({ userId }) => {
  const dispatch = useAppDispatch();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editingNoteId, setEditingNoteId] = useState<string | number | null>(null);
  const [editNoteText, setEditNoteText] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchNotes = async (pageNum = 1, append = false) => {
    try {
      const response = await userService.getNotes(userId, { page: pageNum, limit: 20 });
      if (response.data) {
        const fetchedData = Array.isArray(response.data)
          ? response.data
          : response.data?.docs ||
            response.data?.data ||
            response.data?.notes ||
            response.data?.items ||
            [];
        setNotes((prev) => (append ? [...prev, ...fetchedData] : fetchedData));
        setHasMore(fetchedData.length === 20);
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes(1, false);
  }, [userId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!loading && page === 1) {
      scrollToBottom();
    }
  }, [notes, loading, page]);

  const handleSend = async () => {
    if (!newNote.trim()) return;

    setIsSubmitting(true);
    try {
      await userService.createNote(userId, newNote.trim());
      setNewNote('');
      fetchNotes(1, false);
      dispatch(showToast({ text: 'Note added successfully', type: 'success' }));
    } catch (error: any) {
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to add note',
          type: 'error',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (noteId: string | number) => {
    if (!editNoteText.trim()) return;

    try {
      await userService.updateNote(userId, noteId, editNoteText.trim());
      setEditingNoteId(null);
      setEditNoteText('');
      fetchNotes(1, false);
      dispatch(showToast({ text: 'Note updated successfully', type: 'success' }));
    } catch (error: any) {
      dispatch(
        showToast({
          text: error.response?.data?.message || 'Failed to update note',
          type: 'error',
        }),
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // To display chat style, we might want to reverse notes if they come newest first
  const displayNotes = Array.isArray(notes) ? [...notes].reverse() : [];

  return (
    <Box
      className={styles.tabContent}
      sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: 'calc(100vh - 206px)' }}
    >
      <Typography variant="h6" className={styles.title} sx={{ mb: 2, flexShrink: 0 }}>
        Admin Notes
      </Typography>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          bgcolor: 'rgba(255,255,255,0.02)',
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.05)',
          mb: 2,
        }}
      >
        {displayNotes.length === 0 ? (
          <Typography sx={{ color: 'var(--text-secondary)', textAlign: 'center', mt: 4 }}>
            No notes yet.
          </Typography>
        ) : (
          displayNotes.map((note) => (
            <Box
              key={note.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'var(--bg-panel)',
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography sx={{ color: 'var(--accent-blue)', fontWeight: 600, fontSize: '14px' }}>
                  {note.author?.username || 'Unknown Admin'}
                </Typography>
                <Typography sx={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                  {formatDate(note.createdAt)}
                </Typography>
              </Box>

              {editingNoteId === note.id ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    value={editNoteText}
                    onChange={(e) => setEditNoteText(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'var(--text-primary)',
                        bgcolor: 'rgba(255,255,255,0.05)',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button size="small" onClick={() => setEditingNoteId(null)}>
                      Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={() => handleUpdate(note.id)}>
                      Save
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography
                    sx={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap', fontSize: '14px' }}
                  >
                    {note.text}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, opacity: 0.7 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingNoteId(note.id);
                        setEditNoteText(note.text);
                      }}
                      sx={{ color: 'var(--text-secondary)' }}
                    >
                      <Edit2 size={14} />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexShrink: 0 }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type a note..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'var(--text-primary)',
              bgcolor: 'var(--bg-panel)',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              '&.Mui-focused fieldset': { borderColor: 'var(--accent-blue)' },
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!newNote.trim() || isSubmitting}
          sx={{
            minWidth: '50px',
            height: '56px',
            bgcolor: 'var(--accent-blue)',
            '&:hover': { bgcolor: '#1d84f5' },
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : <Send size={20} />}
        </Button>
      </Box>
    </Box>
  );
};

export default UserNotes;
